import type { Job, JobList, JobSpecPost } from "@eai-toolkit/client";
import { AllowedApi, JobApi } from "@eai-toolkit/client";
import { filtersToQ } from "@store/utils/queryUtils";
import { constructOrderParameter } from "@store/utils/sortUtils";

import { fields } from "../../modules/jobs/data/fields";
import type { LocalJob } from "../../modules/jobs/data/localTypes";
import { jobToLocalType } from "../../modules/jobs/data/localTypes";
import {
  hydrateJobsQueryStateFromString,
  type JobQueryState,
} from "../queryState";
import { baseApi, handleQueryError } from "./baseApi";
import type { ResourcePermissionStatus } from "./types";
import { axiosInstance } from "./utils";

const jobApi = new JobApi(undefined, undefined, axiosInstance);
const allowedApi = new AllowedApi(undefined, undefined, axiosInstance);
const JOBS_TOTAL_LOOKAHEAD_SIZE = 1000;

export const JOB_PERMISSIONS = {
  view: "job:get",
  edit: "job:set",
  write: "job:new",
} as const;

function getPermissionsFromJobQueryState(queryState: JobQueryState) {
  if (queryState.isEditable) {
    return {
      all: [JOB_PERMISSIONS.edit],
      addl: [JOB_PERMISSIONS.write],
    };
  }
  return {
    all: [JOB_PERMISSIONS.view],
    addl: [JOB_PERMISSIONS.edit, JOB_PERMISSIONS.write],
  };
}

// Gets the order field name, if it is different from the field name
function getOrderField(fieldName: string): string {
  const fieldDef = fields[fieldName as keyof typeof fields];
  return fieldDef && "orderField" in fieldDef ? fieldDef.orderField : fieldName;
}

export const jobsApi = baseApi.injectEndpoints({
  endpoints: (builder) => ({
    getJobs: builder.query<
      JobList & { items: LocalJob[] },
      // queryKey is a serialized JobQueryState string. We use a string instead of a JobQueryState
      // object to ensure RTK Query has a stable cache key for reliable caching behavior.
      { queryKey: string }
    >({
      queryFn: async ({ queryKey }) => {
        try {
          const queryState = hydrateJobsQueryStateFromString(queryKey);
          const {
            paging: { offset, limit },
            query: { filters, sort },
          } = queryState;

          const permissions = getPermissionsFromJobQueryState(queryState);
          const order = constructOrderParameter(
            sort.fieldName,
            sort.direction,
            getOrderField,
          );

          const response = await jobApi.v1ClusterJobGet({
            offset,
            limit,
            q: filtersToQ(filters),
            order,
            human: true,
            permission: permissions.all,
          });

          const items = response.data.items.map(jobToLocalType);

          return {
            data: {
              ...response.data,
              items,
            },
          };
        } catch (error) {
          return handleQueryError(error);
        }
      },
      providesTags: (result) =>
        result
          ? [
              ...result.items.map(({ id }) => ({
                type: "Job" as const,
                id,
              })),
              { type: "Job", id: "LIST" },
            ]
          : [{ type: "Job", id: "LIST" }],
    }),

    getJobById: builder.query<
      { data: LocalJob; jobSetStatus: ResourcePermissionStatus },
      { jobId: string }
    >({
      queryFn: async ({ jobId }) => {
        try {
          const response = await jobApi.v1JobGetById({ jobId, human: true });
          const localJob = jobToLocalType(response.data);

          // urn isn't expected in list response for job but it should be expected
          // in the individual response
          const jobUrn = response.data.urn as string;

          // Check if user has job:set permission
          let jobSetStatus: ResourcePermissionStatus = "ok";
          try {
            await allowedApi.v1AllowedPost({
              allowed: { resource: jobUrn, action: JOB_PERMISSIONS.edit },
            });
          } catch (permError) {
            if (
              permError &&
              typeof permError === "object" &&
              "response" in permError
            ) {
              const axiosError = permError as {
                response?: { status?: number };
              };
              if (axiosError.response?.status === 403) {
                jobSetStatus = "not_allowed";
              } else if (
                axiosError.response?.status &&
                axiosError.response.status >= 500
              ) {
                jobSetStatus = "server_error";
              }
            }
          }

          return {
            data: {
              data: localJob,
              jobSetStatus,
            },
          };
        } catch (error) {
          return handleQueryError(error);
        }
      },
      providesTags: (result, error, { jobId }) => [{ type: "Job", id: jobId }],
    }),

    getJobsTotal: builder.query<
      { count: number; isCountFinal: boolean },
      { queryKey: string }
    >({
      queryFn: async ({ queryKey }) => {
        try {
          const queryState = hydrateJobsQueryStateFromString(queryKey);
          const {
            query: { filters },
          } = queryState;

          const response = await jobApi.v1ClusterJobGet({
            q: filtersToQ(filters),
            order: "-created",
            offset: 0,
            limit: JOBS_TOTAL_LOOKAHEAD_SIZE + 1,
          });

          const total = response.data.metadata.total;

          return {
            data: {
              count: Math.min(total, JOBS_TOTAL_LOOKAHEAD_SIZE),
              isCountFinal: total <= JOBS_TOTAL_LOOKAHEAD_SIZE,
            },
          };
        } catch (error) {
          return handleQueryError(error);
        }
      },
    }),

    createJob: builder.mutation<
      LocalJob,
      { accountId: string; jobSpecPost: Partial<JobSpecPost> }
    >({
      queryFn: async ({ accountId, jobSpecPost }) => {
        try {
          const response = await jobApi.v1AccountJobPost({
            accountId,
            jobSpecPost,
            human: true,
          });
          return { data: jobToLocalType(response.data) };
        } catch (error) {
          return handleQueryError(error);
        }
      },
      invalidatesTags: [{ type: "Job", id: "LIST" }],
    }),

    cancelJob: builder.mutation<LocalJob, { jobId: string }>({
      queryFn: async ({ jobId }) => {
        try {
          const response = await jobApi.v1JobDeleteById({ jobId, human: true });
          return { data: jobToLocalType(response.data as unknown as Job) };
        } catch (error) {
          return handleQueryError(error);
        }
      },
      invalidatesTags: (result, error, { jobId }) => [
        { type: "Job", id: jobId },
        { type: "Job", id: "LIST" },
      ],
    }),

    retryJob: builder.mutation<LocalJob, { jobId: string }>({
      queryFn: async ({ jobId }) => {
        try {
          const response = await jobApi.v1JobRetryPut({ jobId, human: true });
          return { data: jobToLocalType(response.data) };
        } catch (error) {
          return handleQueryError(error);
        }
      },
      invalidatesTags: (result, error, { jobId }) => [
        { type: "Job", id: jobId },
        { type: "Job", id: "LIST" },
      ],
    }),

    getJobsPermissionLevels: builder.query<
      Record<string, Record<string, ResourcePermissionStatus>>,
      { queryKey: string }
    >({
      queryFn: async ({ queryKey }) => {
        try {
          const queryState = hydrateJobsQueryStateFromString(queryKey);
          const {
            paging: { offset, limit },
            query: { filters, sort },
          } = queryState;

          const permissions = getPermissionsFromJobQueryState(queryState);
          const order = constructOrderParameter(
            sort.fieldName,
            sort.direction,
            getOrderField,
          );

          // Fetch jobs for all permission levels
          const allPermissions = [...permissions.all, ...permissions.addl];
          const responses = await Promise.all(
            allPermissions.map((permission) =>
              jobApi.v1ClusterJobGet({
                offset,
                limit,
                q: filtersToQ(filters),
                order,
                human: true,
                permission: [permission],
              }),
            ),
          );

          // Build permission lookups for each permission level
          const permissionLookups: Record<
            string,
            Record<string, ResourcePermissionStatus>
          > = {};

          allPermissions.forEach((permission, index) => {
            permissionLookups[permission] = responses[index].data.items.reduce(
              (acc, job) => {
                acc[job.id] = "ok" as ResourcePermissionStatus;
                return acc;
              },
              {} as Record<string, ResourcePermissionStatus>,
            );
          });

          return { data: permissionLookups };
        } catch (error) {
          return handleQueryError(error);
        }
      },
    }),
  }),
});

export const {
  useGetJobsQuery,
  useGetJobByIdQuery,
  useGetJobsTotalQuery,
  useGetJobsPermissionLevelsQuery,
  useCreateJobMutation,
  useCancelJobMutation,
  useRetryJobMutation,
} = jobsApi;
